"use client";

import { FormEvent, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bot,
  Send,
  X,
  Sparkles,
  Loader2,
  Wand2,
  ListTodo,
  Tags,
  Copy,
  Check,
  ArrowDownToLine,
  Replace,
} from "lucide-react";

import { apiRequest } from "@/lib/api";
import { toast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useTasksStore, type TaskPriority } from "@/stores/tasks-store";
import { cn } from "@/lib/utils";

interface AIAssistantPanelProps {
  documentId: string | undefined;
  documentTitle: string;
  documentContent: string;
  onInsert: (text: string) => void;
  onReplace: (text: string) => void;
}

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  payload?: {
    summary?: string;
    improvedText?: string;
    tasks?: { title: string; priority: string }[];
    tags?: string[];
  };
}

interface AIConfig {
  provider: string;
  model: string;
  is_configured: boolean;
}

export function AIAssistantPanel({
  documentId,
  documentTitle,
  documentContent,
  onInsert,
  onReplace,
}: AIAssistantPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<AIConfig | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "welcome",
      role: "assistant",
      content:
        "Hola, soy tu asistente de Wiki. ¿En qué puedo ayudarte con este documento?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingReplace, setPendingReplace] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const createTask = useTasksStore((s) => s.createTask);

  useEffect(() => {
    if (isOpen && config === null) {
      apiRequest<AIConfig>("/v1/ai/admin/config")
        .then(setConfig)
        .catch(() =>
          setConfig({ provider: "openai", model: "gpt-4.1-mini", is_configured: false }),
        );
    }
  }, [isOpen, config]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  function appendMessage(msg: Omit<ChatMessage, "id">) {
    setMessages((prev) => [
      ...prev,
      { ...msg, id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` },
    ]);
  }

  async function sendMessage(text: string) {
    if (!text.trim()) return;
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: text,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    const assistantId = `assistant-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: assistantId, role: "assistant", content: "" },
    ]);

    try {
      const history = [...messages, userMessage]
        .slice(-10)
        .map((m) => ({ role: m.role, content: m.content }));

      const apiBase = process.env.NEXT_PUBLIC_API_URL || "";
      const accessToken =
        typeof window !== "undefined"
          ? window.localStorage.getItem("wiki_access_token")
          : null;

      const res = await fetch(`${apiBase}/v1/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          messages: history,
          scope_document_ids: documentId ? [documentId] : [],
          stream: true,
        }),
      });

      if (!res.ok || !res.body) {
        const errorText = await res.text().catch(() => "Error desconocido");
        throw new Error(errorText || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let accumulated = "";
      let gotError = false;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let lineEnd: number;
        while ((lineEnd = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, lineEnd);
          buffer = buffer.slice(lineEnd + 2);
          const line = rawEvent.replace(/^data:\s*/, "").trim();
          if (!line || line === "[DONE]") continue;
          if (line.startsWith("[ERROR]")) {
            gotError = true;
            throw new Error(line.replace(/^\[ERROR\]\s*/, ""));
          }
          accumulated += line;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content: accumulated } : m,
            ),
          );
        }
      }

      if (accumulated.length === 0) {
        throw new Error("La respuesta de la IA está vacía.");
      }
      if (gotError) {
        throw new Error("La IA devolvió un error.");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "No se pudo obtener respuesta.";
      setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      toast.error("Asistente IA", message);
    } finally {
      setLoading(false);
    }
  }

  async function runAction(action: "summarize" | "improve" | "tasks" | "tags") {
    setLoading(true);
    try {
      switch (action) {
        case "summarize": {
          const result = await apiRequest<{ summary: string }>("/v1/ai/summarize", {
            method: "POST",
            body: JSON.stringify({ text: documentContent }),
          });
          appendMessage({
            role: "assistant",
            content: `**Resumen:**\n\n${result.summary}`,
            payload: { summary: result.summary },
          });
          break;
        }
        case "improve": {
          const result = await apiRequest<{ improved_text: string }>("/v1/ai/improve", {
            method: "POST",
            body: JSON.stringify({ text: documentContent }),
          });
          appendMessage({
            role: "assistant",
            content: `**Texto mejorado:**\n\n${result.improved_text}`,
            payload: { improvedText: result.improved_text },
          });
          break;
        }
        case "tasks": {
          const result = await apiRequest<{ tasks: { title: string; priority: string }[] }>(
            "/v1/ai/tasks",
            {
              method: "POST",
              body: JSON.stringify({ text: documentContent }),
            },
          );
          const tasksText =
            result.tasks.length > 0
              ? result.tasks.map((t, i) => `${i + 1}. ${t.title} (${t.priority})`).join("\n")
              : "_No se detectaron tareas en el documento._";
          appendMessage({
            role: "assistant",
            content: `**Tareas sugeridas:**\n\n${tasksText}`,
            payload: { tasks: result.tasks },
          });
          break;
        }
        case "tags": {
          const result = await apiRequest<{ tags: string[] }>("/v1/ai/tags", {
            method: "POST",
            body: JSON.stringify({ text: documentContent }),
          });
          appendMessage({
            role: "assistant",
            content: `**Tags sugeridos:** ${result.tags.join(", ")}`,
            payload: { tags: result.tags },
          });
          break;
        }
      }
    } catch (err) {
      toast.error(
        "Asistente IA",
        err instanceof Error ? err.message : "No se pudo completar la acción.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateTasksFromAI(tasks: { title: string; priority: string }[]) {
    if (!documentId) {
      toast.warning("Abre un documento", "Vincula un documento al chat para crear tareas con asociación.");
    }
    const priorities: TaskPriority[] = ["low", "medium", "high"];
    let count = 0;
    for (const t of tasks) {
      try {
        const prio = priorities.includes(t.priority as TaskPriority)
          ? (t.priority as TaskPriority)
          : "medium";
        await createTask({
          document_id: documentId ?? null,
          title: t.title,
          priority: prio,
        });
        count++;
      } catch (err) {
        toast.error("No se pudo crear la tarea", err instanceof Error ? err.message : "");
      }
    }
    if (count > 0) {
      toast.success(`${count} tarea${count !== 1 ? "s" : ""} creada${count !== 1 ? "s" : ""}`);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    sendMessage(input);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label="Abrir asistente de IA"
        className={cn(
          "fixed bottom-6 right-6 z-40 flex items-center gap-2 h-11 px-4 rounded-full shadow-lg shadow-primary/20 transition-all",
          config?.is_configured
            ? "bg-primary text-primary-foreground hover:bg-primary/90"
            : "bg-muted text-muted-foreground",
        )}
      >
        <Bot className="w-5 h-5" />
        <span className="text-sm font-medium">Asistente IA</span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 320 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 320 }}
            transition={{ type: "spring", damping: 22, stiffness: 220 }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[420px] z-50 bg-card border-l border-border shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-4 h-14 border-b border-border/40 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <Bot className="w-5 h-5 text-primary shrink-0" />
                <span className="font-medium truncate">Asistente IA</span>
                {config?.is_configured ? (
                  <span className="text-[10px] bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full shrink-0">
                    {config.provider}
                  </span>
                ) : (
                  <span className="text-[10px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded-full shrink-0">
                    no configurado
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Cerrar asistente"
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 p-3 border-b border-border/40 shrink-0">
              <QuickAction
                icon={Sparkles}
                label="Resumir"
                onClick={() => runAction("summarize")}
                disabled={loading}
              />
              <QuickAction
                icon={Wand2}
                label="Mejorar"
                onClick={() => runAction("improve")}
                disabled={loading}
              />
              <QuickAction
                icon={ListTodo}
                label="Tareas"
                onClick={() => runAction("tasks")}
                disabled={loading}
              />
              <QuickAction
                icon={Tags}
                label="Tags"
                onClick={() => runAction("tags")}
                disabled={loading}
              />
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={cn(
                    "flex gap-2",
                    msg.role === "user" ? "justify-end" : "justify-start",
                  )}
                >
                  {msg.role === "assistant" && (
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                  )}
                  <div className="max-w-[85%] space-y-2">
                    <div
                      className={cn(
                        "rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap",
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground rounded-br-md"
                          : "bg-secondary text-secondary-foreground rounded-bl-md",
                      )}
                    >
                      {msg.content}
                    </div>
                    {msg.payload && (
                      <MessageActions
                        payload={msg.payload}
                        onInsert={(text) => {
                          onInsert(text);
                          toast.success("Insertado al editor");
                        }}
                        onReplace={(text) => setPendingReplace(text)}
                        onCreateTasks={(tasks) => handleCreateTasksFromAI(tasks)}
                      />
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                  <div className="bg-secondary rounded-2xl rounded-bl-md px-3.5 py-2 flex items-center gap-1.5">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm text-muted-foreground">Pensando...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSubmit} className="p-3 border-t border-border/40 shrink-0">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Pregunta sobre este documento..."
                  aria-label="Mensaje para el asistente"
                  className="flex-1 h-10 px-3 bg-background border border-input rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  aria-label="Enviar mensaje"
                  className="h-10 w-10 flex items-center justify-center rounded-lg bg-primary text-primary-foreground disabled:opacity-50 hover:bg-primary/90 transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={pendingReplace !== null}
        onOpenChange={(open) => !open && setPendingReplace(null)}
        title="Reemplazar contenido del editor"
        description="Esta acción sobrescribirá todo el contenido actual del documento con la respuesta de la IA. No se puede deshacer."
        confirmText="Sí, reemplazar"
        cancelText="Cancelar"
        variant="destructive"
        onConfirm={() => {
          if (pendingReplace !== null) {
            onReplace(pendingReplace);
            toast.success("Editor reemplazado");
            setPendingReplace(null);
          }
        }}
      />
    </>
  );
}

function QuickAction({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center gap-1.5 h-9 px-2 rounded-lg bg-secondary/50 hover:bg-secondary text-xs font-medium transition-colors disabled:opacity-50"
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function MessageActions({
  payload,
  onInsert,
  onReplace,
  onCreateTasks,
}: {
  payload: NonNullable<ChatMessage["payload"]>;
  onInsert: (text: string) => void;
  onReplace: (text: string) => void;
  onCreateTasks: (tasks: { title: string; priority: string }[]) => void;
}) {
  const [copied, setCopied] = useState(false);

  const insertable = payload.summary ?? payload.improvedText;

  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-1">
      {insertable && (
        <>
          <ActionPill icon={ArrowDownToLine} label="Insertar" onClick={() => onInsert(insertable)} />
          <ActionPill icon={Replace} label="Reemplazar" onClick={() => onReplace(insertable)} />
          <ActionPill
            icon={copied ? Check : Copy}
            label={copied ? "Copiado" : "Copiar"}
            onClick={() => {
              navigator.clipboard.writeText(insertable);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          />
        </>
      )}
      {payload.tasks && payload.tasks.length > 0 && (
        <ActionPill
          icon={ListTodo}
          label={`Crear ${payload.tasks.length} tarea${payload.tasks.length !== 1 ? "s" : ""}`}
          onClick={() => onCreateTasks(payload.tasks!)}
        />
      )}
    </div>
  );
}

function ActionPill({
  icon: Icon,
  label,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-background border border-border/40 hover:border-primary/40 hover:bg-primary/5 text-[11px] font-medium text-foreground/80 transition-colors"
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}
